import React from 'react';
import { Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, Panel, SearchField, SegmentedControl, StatusChip, type ListColumn,
} from '../../../ui-kit';
import { fetchStlLeads, watchStlLeads, type StlLeadCard } from '../../../services/speedToLeadService';
import { fromNow, matches, useLive, when } from '../rocco/live';
import LeadStoryModal from './LeadStory';
import { liveChip, statusChip } from './status';

type Scope = 'live' | 'done';

/** New leads still being worked: emailed, a call coming up, or on a call now. Updates as things happen. */
export function LiveLeadsPage() {
  return <LeadsPage scope="live" />;
}

/** Every finished lead: talked to the specialist, a call booked, a call task, closed, or with a person. */
export function DoneLeadsPage() {
  return <LeadsPage scope="done" />;
}

const FILTERS: Record<Scope, { value: string; label: string; test: (l: StlLeadCard) => boolean }[]> = {
  live: [
    { value: 'all', label: 'All', test: () => true },
    { value: 'calling', label: 'On a call', test: (l) => l.status === 'calling' },
    { value: 'waiting', label: 'Call coming up', test: (l) => l.status === 'waiting' || l.status === 'new' },
  ],
  done: [
    { value: 'all', label: 'All', test: () => true },
    { value: 'transferred', label: 'Talked to a specialist', test: (l) => l.status === 'transferred' },
    { value: 'scheduled', label: 'Call booked', test: (l) => l.status === 'scheduled' },
    { value: 'follow_up', label: 'Specialist will call', test: (l) => l.status === 'follow_up' },
    { value: 'person', label: 'Needs a person', test: (l) => l.status === 'needs_person' },
    { value: 'closed', label: 'Not interested', test: (l) => l.status === 'closed' },
  ],
};

/** Where a lead stands, in words. */
function standing(l: StlLeadCard): string {
  if (l.status === 'needs_person') return l.needs_person_reason ?? 'Needs a person';
  if (l.status === 'waiting' && l.next_call_at) {
    return l.calls ? `No answer. Calling again ${fromNow(l.next_call_at)}` : `Emailed. Calling ${fromNow(l.next_call_at)}`;
  }
  if (l.status === 'new') return 'Sending the first email';
  if (l.status === 'calling') return l.calls > 1 ? `Call ${l.calls}` : 'First call';
  return l.outcome ?? '';
}

function LeadsPage({ scope }: { scope: Scope }) {
  const live = scope === 'live';
  const fetcher = React.useCallback(() => fetchStlLeads(scope), [scope]);
  const { data, error, loading, streaming, reconnecting } = useLive(fetcher, live ? 5000 : 15_000, { watch: watchStlLeads });
  const [open, setOpen] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [show, setShow] = React.useState('all');
  const all = data?.leads ?? [];
  const filters = FILTERS[scope];
  const chosen = filters.find((f) => f.value === show) ?? filters[0];
  const rows = all.filter(chosen.test).filter((l) => matches(query, [
    l.name, l.email, l.phone, l.outcome, l.specialist_name, l.needs_person_reason, statusChip(l.status).label,
  ]));

  const columns: ListColumn<StlLeadCard>[] = [
    {
      key: 'lead', sortValue: (l) => l.name, header: 'Lead', grow: 1, minWidth: 180,
      render: (l) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{l.name}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
            {[l.phone, l.email].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'arrived', sortValue: (l) => l.arrived_at, sortStartsDescending: true, header: 'Arrived', width: 140,
      render: (l) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
            {when(l.arrived_at)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fromNow(l.arrived_at)}</Typography>
        </Stack>
      ),
    },
    {
      key: 'status', sortValue: (l) => statusChip(l.status).label, header: live ? 'Now' : 'Result', width: 200,
      render: (l) => (
        <Stack spacing={0.25} alignItems="flex-start">
          <StatusChip {...(live ? liveChip(l) : statusChip(l.status))} pulse={live && l.status === 'calling'} />
          {!live && l.completed_at ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{when(l.completed_at)}</Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'standing', sortValue: (l) => l.completed_at || l.next_call_at, header: live ? 'Where it stands' : 'What happened',
      grow: 1.6, minWidth: 220,
      render: (l) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{standing(l)}</Typography>
          {l.specialist_name && !live ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {`With ${l.specialist_name}${l.in_agencyzoom ? ' · in AgencyZoom' : ''}`}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
  ];

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(l) => l.id}
      pageSize={25}
      loading={loading}
      onRowClick={(l) => setOpen(l.id)}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search or the filter.'
        : live
          ? 'No leads being worked. A new HubSpot contact on the chosen email domains appears here within seconds.'
          : 'No finished leads yet. A lead lands here once it has talked to a specialist, has a call booked, or is handed on.'}
    />
  );
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search leads…" />;
  const picker = (
    <SegmentedControl
      value={chosen.value}
      onChange={setShow}
      ariaLabel="Which leads"
      options={filters.map((f) => ({ value: f.value, label: `${f.label} · ${all.filter(f.test).length}` }))}
    />
  );
  const state = reconnecting ? 'Reconnecting…' : streaming ? 'live' : 'updates by itself';
  const count = (status: StlLeadCard['status']) => all.filter((l) => l.status === status).length;
  const subtitle = live
    ? `${all.length} being worked · ${state}`
    : `${count('transferred')} talked to a specialist · ${count('scheduled')} calls booked · `
      + `${count('needs_person')} with a person · newest first`;

  return (
    <Stack spacing={2}>
      <Panel
        title={live ? 'Live leads' : 'Finished leads'}
        subtitle={subtitle}
        search={searchBox}
        tools={picker}
        expanded={{ content: table, actions: <>{searchBox}{picker}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : table}
      </Panel>
      {open ? <LeadStoryModal leadId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
